/**
 * PicksFiltersSection Component
 * View mode pills + filter controls for the Picks Data Dashboard
 * Includes basic filters (search, date, outcome, mode, direction) and advanced filters (collapsible)
 */

import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type { PicksFilters, ViewMode } from '@server/services/admin/adminPicksTypes.ts';
import { ChevronDown, ChevronUp, Database, DollarSign, Star, X } from 'lucide-react';
import { useState } from 'react';

interface PicksFiltersSectionProps {
  viewMode: ViewMode;
  filters: PicksFilters;
  filterOptions: {
    propTypes: string[];
    sportsbooks: string[];
    configVariations: string[];
  } | null;
  onViewModeChange: (mode: ViewMode) => void;
  onFiltersChange: (filters: PicksFilters) => void;
}

export function PicksFiltersSection({
  viewMode,
  filters,
  filterOptions,
  onViewModeChange,
  onFiltersChange,
}: PicksFiltersSectionProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const updateFilter = (key: keyof PicksFilters, value: unknown) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-4">
      {/* View Mode Pills */}
      <div className="flex flex-wrap gap-2">
        <ViewModePill
          icon={<Database className="h-4 w-4" />}
          label="All Picks"
          active={viewMode === 'all'}
          onClick={() => onViewModeChange('all')}
        />
        <ViewModePill
          icon={<Star className="h-4 w-4" />}
          label="Tracked Only"
          active={viewMode === 'tracked'}
          onClick={() => onViewModeChange('tracked')}
        />
        <ViewModePill
          icon={<DollarSign className="h-4 w-4" />}
          label="Real Bets"
          active={viewMode === 'real_bets'}
          onClick={() => onViewModeChange('real_bets')}
        />
      </div>

      {/* Set by a pick's View Parlay or View Generation link, which have no control of their own */}
      {(filters.parlayId !== undefined || filters.generationId) && (
        <div className="flex items-center gap-2 text-sm text-theme-text-secondary">
          <span>
            {filters.parlayId !== undefined
              ? `Showing one parlay (#${filters.parlayId})`
              : `Showing one generation (${filters.generationId?.slice(0, 8)})`}
          </span>
          <button
            type="button"
            onClick={() =>
              onFiltersChange({ ...filters, parlayId: undefined, generationId: undefined })
            }
            aria-label="Show all picks again"
            className="rounded p-1 text-theme-text-secondary hover:bg-theme-surface-hover hover:text-theme-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Basic Filters */}
      <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2 @5xl:grid-cols-5">
        {/* Search */}
        <input
          type="text"
          placeholder="Search player or team..."
          value={filters.search || ''}
          onChange={(e) => updateFilter('search', e.target.value || undefined)}
          className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text placeholder-theme-text-tertiary focus:border-theme-primary focus:outline-none"
        />

        {/* Outcome */}
        <select
          value={filters.outcome || 'all'}
          onChange={(e) =>
            updateFilter('outcome', e.target.value === 'all' ? undefined : e.target.value)
          }
          className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
        >
          <option value="all">All Outcomes</option>
          <option value="pending">Pending</option>
          <option value="hit">Hit</option>
          <option value="miss">Miss</option>
          <option value="push">Push</option>
        </select>

        {/* Analysis Mode */}
        <select
          value={filters.analysisMode || 'all'}
          onChange={(e) =>
            updateFilter('analysisMode', e.target.value === 'all' ? undefined : e.target.value)
          }
          className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
        >
          <option value="all">All Modes</option>
          <option value="standard">Standard</option>
          <option value="detailed">Detailed</option>
          <option value="premium">Premium</option>
        </select>

        {/* Direction */}
        <select
          value={filters.direction || 'all'}
          onChange={(e) =>
            updateFilter('direction', e.target.value === 'all' ? undefined : e.target.value)
          }
          className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
        >
          <option value="all">All Directions</option>
          <option value="over">Over</option>
          <option value="under">Under</option>
          <option value="other">Other</option>
        </select>

        {/* Date Range (from) */}
        <input
          type="date"
          value={filters.dateFrom || ''}
          onChange={(e) => updateFilter('dateFrom', e.target.value || undefined)}
          className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
        />
      </div>

      {/* Advanced Filters (Collapsible) */}
      <div className="rounded-lg border border-theme-border bg-theme-surface">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-theme-text hover:bg-theme-bg"
        >
          <span>Advanced Filters</span>
          {advancedOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {advancedOpen && (
          <div className="grid grid-cols-1 gap-4 border-t border-theme-border p-4 @3xl:grid-cols-2 @5xl:grid-cols-4">
            {/* Prop Type (multi-select) */}
            <div>
              <label className="mb-1 block text-xs font-medium text-theme-text-secondary">
                Prop Type
              </label>
              <select
                multiple
                value={filters.propType || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                  updateFilter('propType', selected.length > 0 ? selected : undefined);
                }}
                className="h-32 w-full rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
              >
                {filterOptions?.propTypes.map((type) => (
                  <option key={type} value={type}>
                    {formatStatLabel(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* AI Edge */}
            <div>
              <label className="mb-1 block text-xs font-medium text-theme-text-secondary">
                AI Edge
              </label>
              <select
                value={filters.aiEdge || 'all'}
                onChange={(e) =>
                  updateFilter('aiEdge', e.target.value === 'all' ? undefined : e.target.value)
                }
                className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
              >
                <option value="all">All</option>
                <option value="Strong">Strong</option>
                <option value="Moderate">Moderate</option>
                <option value="Slight">Slight</option>
              </select>
            </div>

            {/* AI Confidence */}
            <div>
              <label className="mb-1 block text-xs font-medium text-theme-text-secondary">
                AI Confidence
              </label>
              <select
                value={filters.aiConfidence || 'all'}
                onChange={(e) =>
                  updateFilter(
                    'aiConfidence',
                    e.target.value === 'all' ? undefined : e.target.value,
                  )
                }
                className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
              >
                <option value="all">All</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            {/* Sportsbook */}
            <div>
              <label className="mb-1 block text-xs font-medium text-theme-text-secondary">
                Sportsbook
              </label>
              <select
                value={filters.sportsbook || ''}
                onChange={(e) => updateFilter('sportsbook', e.target.value || undefined)}
                className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
              >
                <option value="">All</option>
                {filterOptions?.sportsbooks.map((book) => (
                  <option key={book} value={book}>
                    {book}
                  </option>
                ))}
              </select>
            </div>

            {/* Min Score */}
            <div>
              <label className="mb-1 block text-xs font-medium text-theme-text-secondary">
                Min Strategy Score
              </label>
              <input
                type="number"
                step="0.1"
                value={filters.minScore ?? ''}
                onChange={(e) =>
                  updateFilter('minScore', e.target.value ? parseFloat(e.target.value) : undefined)
                }
                className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
              />
            </div>

            {/* Max Score */}
            <div>
              <label className="mb-1 block text-xs font-medium text-theme-text-secondary">
                Max Strategy Score
              </label>
              <input
                type="number"
                step="0.1"
                value={filters.maxScore ?? ''}
                onChange={(e) =>
                  updateFilter('maxScore', e.target.value ? parseFloat(e.target.value) : undefined)
                }
                className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
              />
            </div>

            {/* Was Flipped */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={filters.wasFlipped ?? false}
                  onChange={(e) => updateFilter('wasFlipped', e.target.checked || undefined)}
                  className="h-4 w-4 rounded border-theme-border bg-theme-surface text-theme-primary focus:ring-2 focus:ring-theme-primary"
                />
                <span className="text-sm text-theme-text">Direction Flipped</span>
              </label>
            </div>

            {/* Config Variation */}
            <div>
              <label className="mb-1 block text-xs font-medium text-theme-text-secondary">
                Config Variation
              </label>
              <select
                value={filters.configVariation || ''}
                onChange={(e) => updateFilter('configVariation', e.target.value || undefined)}
                className="rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-sm text-theme-text focus:border-theme-primary focus:outline-none"
              >
                <option value="">All</option>
                {filterOptions?.configVariations.map((config) => (
                  <option key={config} value={config}>
                    {config}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface ViewModePillProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

function ViewModePill({ icon, label, active, onClick }: ViewModePillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'bg-theme-primary-fill text-theme-on-primary-fill'
          : 'border border-theme-border bg-theme-surface text-theme-text hover:bg-theme-bg'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
